import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColorValue, SettingField, SettingValue } from '@aquilixshop/ui-react';
import { useBuilderStore } from '../state/builderStore';
import { FieldControl } from '../inspector/FieldControl';
import { themeTokenHex } from '../inspector/controls/colorUtils';
import { FieldTargetProvider } from '../inspector/fieldTarget';
import { saveCountdownDesign } from '../lib/countdownApi';
import { useSellerCan } from '../lib/sellerPermissions';
import { bt, groupLabel, translateFields } from '../i18n';

/**
 * THE LOOK OF THE COUNTDOWN, SET IN THE BUILDER (feature 0041 V7, 29/09/2026).
 *
 * « I can't select and adapt its design in the builder — the builder is just for the design like font
 * size font weight colors padding ». So: the app keeps WHEN the countdown runs, where it shows and what
 * it says; this panel is HOW IT LOOKS, and it writes to the same campaign the dashboard's own editor
 * writes (her call: the two screens edit one setting, not two).
 *
 * ## Why it reuses the inspector's controls
 *
 * The fields below are an ordinary `SettingField[]`, rendered by the same `FieldControl` every section
 * uses. That is not laziness: it is what makes this panel look and behave like the rest of the editor —
 * the same colour picker with its swatches and its "Automatic" state, the same slider with its keyboard
 * steps, the same translated labels through `translateFields`. A bespoke form here would be a second
 * design system in the same rail.
 *
 * ## Why the canvas repaints before the save lands
 *
 * `patchCountdownDesign` applies the change to the payloads the canvas paints from, immediately; the PUT
 * follows, debounced. A colour picker that repaints a quarter-second after the drag is one nobody can
 * aim with. A failed save surfaces as a message — it never silently leaves the canvas showing something
 * the shop will not serve.
 */

/** The design as the wire carries it: a preset name, hex colours, a size and a weight. */
export interface CountdownDesign {
  preset?: string;
  colors?: Record<string, string>;
  textSize?: number;
  textWeight?: number;
  /** The CLOCK's size in px (10–72) — its own setting, not a ratio of `textSize`. */
  digitSize?: number;
}

/** The six colour slots the app's surfaces know — mirrored from the domain's `COLOR_KEYS`. */
const COLOR_SLOTS = [
  { id: 'background', label: 'Background' },
  { id: 'text', label: 'Text' },
  { id: 'digit', label: 'Digits' },
  { id: 'digitBackground', label: 'Digit background' },
  { id: 'button', label: 'Button' },
  { id: 'buttonText', label: 'Button label' },
] as const;

const GROUP_TYPE = 'Typography';
const GROUP_COLORS = 'Countdown colors';

/**
 * The fields, in the shape the inspector renders. `auto: true` on every colour: untouched, the surface
 * uses its PRESET's own colour, and the picker says "Automatic" rather than showing a colour nobody
 * chose (the 2026-09-11 rule — an untouched field must render what the inspector shows).
 */
const FIELDS: SettingField[] = [
  {
    id: 'preset',
    type: 'select',
    label: 'Preset',
    group: GROUP_TYPE,
    default: 'circles',
    options: [
      { value: 'circles', label: 'Circles' },
      { value: 'plain', label: 'Plain' },
      { value: 'boxed', label: 'Boxed' },
      { value: 'dark', label: 'Dark' },
      { value: 'neon', label: 'Neon' },
      { value: 'brand', label: 'Brand' },
      { value: 'night', label: 'Night' },
      { value: 'mint', label: 'Mint' },
    ],
  },
  {
    id: 'textSize',
    type: 'range',
    label: 'Text size',
    group: GROUP_TYPE,
    default: 13,
    min: 10,
    max: 40,
    step: 1,
    unit: 'px',
  },
  {
    id: 'digitSize',
    type: 'range',
    label: 'Digit size',
    group: GROUP_TYPE,
    // The strip's own default. The bubble paints 17 and the banner 15/20 — a slider shows ONE number, and
    // the surface this panel was opened from is the one the seller is looking at.
    default: 18,
    // The engine's `DIGIT_SIZE_MIN/MAX`, mirrored (the panel already mirrors `COLOR_KEYS` the same way).
    min: 10,
    max: 72,
    step: 1,
    unit: 'px',
  },
  {
    id: 'textWeight',
    type: 'select',
    label: 'Text weight',
    group: GROUP_TYPE,
    default: '700',
    options: [
      { value: '400', label: 'Regular' },
      { value: '500', label: 'Medium' },
      { value: '600', label: 'Semi bold' },
      { value: '700', label: 'Bold' },
      { value: '800', label: 'Extra bold' },
    ],
  },
  ...COLOR_SLOTS.map(
    (slot): SettingField => ({
      id: slot.id,
      type: 'color',
      label: slot.label,
      group: GROUP_COLORS,
      auto: true,
    }),
  ),
];

/** Wire design → the values the controls read. Colours are flattened out of their `colors` map. */
function toValues(design: CountdownDesign): Record<string, SettingValue | undefined> {
  const values: Record<string, SettingValue | undefined> = {
    preset: design.preset,
    textSize: design.textSize,
    digitSize: design.digitSize,
    textWeight: design.textWeight === undefined ? undefined : String(design.textWeight),
  };
  for (const slot of COLOR_SLOTS) {
    const hex = design.colors?.[slot.id];
    values[slot.id] = hex ? ({ ref: 'custom', value: hex } as ColorValue) : undefined;
  }
  return values;
}

/** A control's answer → the wire patch for that one field. `undefined` clears it back to the preset. */
export function designPatchFor(
  fieldId: string,
  next: SettingValue | undefined,
  /** How a theme slot becomes a hex — injectable so the unit test does not need a document. */
  resolveToken: (token: string) => string = themeTokenHex,
): CountdownDesign {
  if (fieldId === 'preset') return { preset: typeof next === 'string' ? next : undefined };
  if (fieldId === 'textSize') return { textSize: typeof next === 'number' ? next : undefined };
  if (fieldId === 'digitSize') return { digitSize: typeof next === 'number' ? next : undefined };
  if (fieldId === 'textWeight') {
    const parsed = Number(next);
    return { textWeight: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined };
  }
  /*
   * A CAMPAIGN IS STORED IN HEX, AND THE PICKER SPEAKS TWO LANGUAGES (04/10/2026).
   *
   * The source dropdown offers eleven entries: "Automatic", nine THEME slots, and "Custom…". A theme slot
   * writes `{ref:'theme',token}` — no `value` — so reading `next.value` found nothing and the patch cleared
   * the slot instead of colouring it. Nine of the eleven entries did nothing at all, measured: pick
   * "Accent 1" on the background and the strip stayed black. « les countdown colors config ils sont tous
   * mort ».
   *
   * The countdown is not a section of the document: it is a campaign of the app, published as plain hex to
   * a bar that is mounted OUTSIDE the themed page root, where `--aqx-theme-*` does not even exist. So a
   * theme slot is resolved to the colour it is painting RIGHT NOW and stored as that hex — the seller gets
   * the colour she picked, and the storefront gets something it can render.
   */
  const hex =
    next && typeof next === 'object' && 'ref' in next
      ? next.ref === 'theme' && typeof next.token === 'string'
        ? resolveToken(next.token)
        : 'value' in next && typeof next.value === 'string'
          ? next.value
          : undefined
      : undefined;
  return { colors: { [fieldId]: hex ?? '' } };
}

/** Merge a patch onto a design, dropping the keys the seller cleared. */
export function mergeDesign(current: CountdownDesign, patch: CountdownDesign): CountdownDesign {
  const next: CountdownDesign = { ...current };
  if ('preset' in patch) {
    if (patch.preset === undefined) delete next.preset;
    else next.preset = patch.preset;
  }
  if ('textSize' in patch) {
    if (patch.textSize === undefined) delete next.textSize;
    else next.textSize = patch.textSize;
  }
  if ('digitSize' in patch) {
    if (patch.digitSize === undefined) delete next.digitSize;
    else next.digitSize = patch.digitSize;
  }
  if ('textWeight' in patch) {
    if (patch.textWeight === undefined) delete next.textWeight;
    else next.textWeight = patch.textWeight;
  }
  if (patch.colors) {
    const colors = { ...(current.colors ?? {}) };
    for (const [key, value] of Object.entries(patch.colors)) {
      // An empty string is the picker's "Automatic": the KEY GOES AWAY, so the preset's own colour wins
      // again. Storing '' would paint an invalid colour and the backend would drop it anyway.
      if (value === '') delete colors[key];
      else colors[key] = value;
    }
    if (Object.keys(colors).length > 0) next.colors = colors;
    else delete next.colors;
  }
  return next;
}

/** Read the design off whichever payload carries the selected campaign. */
function designOf(payload: unknown, campaignId: string): CountdownDesign | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  if (p.campaignId !== campaignId) return null;
  return (p.design ?? {}) as CountdownDesign;
}

const SAVE_DEBOUNCE_MS = 400;

export function CountdownDesignPanel({
  campaignId,
  surface,
}: {
  campaignId: string;
  surface: 'bar' | 'popup';
}) {
  const bar = useBuilderStore((s) => s.countdownBar);
  const popup = useBuilderStore((s) => s.countdownPopup);
  const patchCountdownDesign = useBuilderStore((s) => s.patchCountdownDesign);
  const [error, setError] = useState<string | null>(null);
  /*
   * The save follows `countdown:manage`, not `theme:manage` (feature 0073). Without the box, the canvas used
   * to repaint on every drag and the shop was never told — « Could not save » arrived after the work. The
   * panel now shows the look read-only and says which box opens it. `pending` locks too: a drag in the
   * hundred milliseconds before the answer would be the same lost work.
   */
  const canEdit = useSellerCan('countdown:manage');

  const design = useMemo(
    () => designOf(surface === 'bar' ? bar : popup, campaignId) ?? {},
    [bar, popup, campaignId, surface],
  );
  const values = useMemo(() => toValues(design), [design]);

  /**
   * The save is debounced, and the LAST design wins. A colour drag fires a dozen changes a second; each
   * one repaints the canvas (that is the point) but only the last one needs to reach the shop.
   */
  const pending = useRef<CountdownDesign | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const onChange = useCallback(
    (fieldId: string, next: SettingValue | undefined) => {
      if (canEdit !== 'yes') return;
      const merged = mergeDesign(pending.current ?? design, designPatchFor(fieldId, next));
      pending.current = merged;
      // Repaint FIRST: the canvas is the answer to "what will this look like", and it must not wait on
      // a round trip. `colors` is replaced wholesale so a cleared slot disappears from the canvas too.
      patchCountdownDesign(campaignId, { ...merged, colors: merged.colors ?? {} });
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        const toSave = pending.current;
        pending.current = null;
        if (!toSave) return;
        void saveCountdownDesign(campaignId, { ...toSave } as Record<string, unknown>).then(
          (ok) => {
            setError(ok ? null : bt('chrome.cdpanel.save_failed', 'Could not save. Try again.'));
          },
        );
      }, SAVE_DEBOUNCE_MS);
    },
    [campaignId, canEdit, design, patchCountdownDesign],
  );

  const fields = useMemo(() => translateFields('countdown-app', FIELDS), []);
  const groups = useMemo(() => {
    const out = new Map<string, SettingField[]>();
    for (const field of fields) {
      const key = field.group ?? GROUP_TYPE;
      out.set(key, [...(out.get(key) ?? []), field]);
    }
    return [...out.entries()];
  }, [fields]);

  return (
    <FieldTargetProvider target="countdown-app">
      <p className="bld-inspector__note">
        {bt(
          'chrome.cdpanel.note',
          'The timer, its targeting and its texts are set in the Countdown app. Here you set how it looks.',
        )}
      </p>
      {canEdit === 'no' ? (
        <p className="bld-inspector__note" role="status" data-testid="cdpanel-no-right">
          {bt(
            'chrome.cdpanel.no_right',
            'To change this look, you need the “Manage the countdown” right. Ask the person who owns the shop: they can add it to your role from Settings, Roles.',
          )}
        </p>
      ) : null}
      {error ? (
        <p className="bld-inspector__error" role="alert">
          {error}
        </p>
      ) : null}
      <fieldset className="bld-cdpanel__fields" disabled={canEdit !== 'yes'}>
        {groups.map(([name, groupFields]) => (
          <section key={name} className="bld-grp">
            <h3 className="bld-grp__title">{groupLabel('countdown-app', name)}</h3>
            <div className="bld-form">
              {groupFields.map((field) => (
                <FieldControl
                  key={field.id}
                  field={field}
                  value={values[field.id]}
                  onChange={(next) => onChange(field.id, next as SettingValue | undefined)}
                />
              ))}
            </div>
          </section>
        ))}
      </fieldset>
    </FieldTargetProvider>
  );
}
